<template>
  <div>

    <!-- Formulaire global -->
    <b-form @submit.prevent="handleSubmit">

      <b-row>
        <!-- Colonne gauche (sticky) -->
        <b-col md="4">
          <!-- Carte “General Info” -->
          <b-card no-body class="left-sticky-card mb-3">
            <b-card-header>
              <!-- On place le titre + le bouton alignés sur la même ligne -->
              <div class="d-flex justify-content-between align-items-center">
                <span>{{ $t("General info") }}</span>
                <!-- Bouton Submit -->
                <b-button
                  :disabled="loading"
                  variant="outline-primary"
                  class="btn-round"
                  type="submit"
                >
                  <span
                    v-if="loading"
                    class="spinner-border spinner-border-sm"
                    role="status"
                  ></span>
                  {{ isEditing ? "Save" : "Create Company" }}
                </b-button>
              </div>
            </b-card-header>

            <b-card-body>
              <!-- Name -->
              <b-form-group
                label="Name"
                :state="getFieldState('name')"
                invalid-feedback="Name is required"
              >
                <b-form-input
                  v-model="form.name"
                  :state="getFieldState('name')"
                  required
                />
              </b-form-group>

              <!-- Type -->
              <b-form-group
                label="Type"
                :state="getFieldState('type')"
                invalid-feedback="Type is required"
              >
                <b-form-select
                  v-model="form.type"
                  :options="formattedCompanyTypes"
                  :state="getFieldState('type')"
                  required
                />
              </b-form-group>

              <!-- Code -->
              <b-form-group
                label="Code"
                :state="getFieldState('company_code')"
                invalid-feedback="Code is required"
              >
                <b-form-input
                  v-model="form.company_code"
                  :state="getFieldState('company_code')"
                />
              </b-form-group>

              <!-- Logo -->
              <b-form-group label="Logo">
                <file-manager
                  title=""
                  resource-type="company"
                  :resource-id="form.id"
                />
              </b-form-group>
            </b-card-body>
          </b-card>

        </b-col>

        <!-- Colonne droite -->
        <b-col md="8">
          <!-- Carte : Address & Location -->
          <b-card no-body class="mb-3">
            <b-card-header>{{ $t("Address & Location") }}</b-card-header>
            <b-card-body>
              <b-form-group
                label="Address"
                :state="getFieldState('address')"
                invalid-feedback="Address is required"
              >
                <b-form-textarea
                  v-model="form.address"
                  :state="getFieldState('address')"
                  rows="3"
                  required
                />
              </b-form-group>

              <b-row>
                <b-col md="6">
                  <b-form-group
                    label="City"
                    :state="getFieldState('city')"
                    invalid-feedback="City is required"
                  >
                    <b-form-input
                      v-model="form.city"
                      :state="getFieldState('city')"
                      required
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    label="Zipcode"
                    :state="getFieldState('zipcode')"
                    invalid-feedback="Zipcode is required"
                  >
                    <b-form-input
                      v-model="form.zipcode"
                      :state="getFieldState('zipcode')"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="6">
                  <b-form-group
                    label="Country"
                    :state="getFieldState('country_id')"
                    invalid-feedback="Country is required"
                  >
                    <b-form-select
                      v-model="form.country_id"
                      :options="formattedCountries"
                      :state="getFieldState('country_id')"
                      required
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    label="Production Place"
                    :state="getFieldState('production_place')"
                    invalid-feedback="Production Place is required"
                  >
                    <b-form-select
                      v-model="form.production_place"
                      :options="productionPlaceOptions"
                      :state="getFieldState('production_place')"
                      required
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>

          <!-- Carte : Connected Companies -->
          <b-card no-body class="mb-3">
            <b-card-header>{{ $t("Connected Companies") }}</b-card-header>
            <b-card-body>
              <companies-connect
                :selected-ids="form.companies"
                :company-id="form.id"
                @update:selectedIds="updateConnectedCompanies"
              />
            </b-card-body>
          </b-card>

          <!-- Carte : Client Options (si type = client) -->
          <b-card no-body class="mb-3" v-if="form.type === 'client'">
            <b-card-header>{{ $t("Client Options") }}</b-card-header>
            <b-card-body>
              <b-row>
                <b-col md="6">
                  <b-form-group>
                    <div class="d-flex align-items-center">
                      <b-form-checkbox
                        v-model="form.with_passports"
                        switch
                        class="mr-2"
                      />
                      <span>{{ $t("enable_passports") }}</span>
                    </div>
                  </b-form-group>
                </b-col>

                <b-col md="6">
                  <b-form-group
                    label="Passport Limit"
                    :state="getFieldState('passport_limit')"
                    invalid-feedback="Passport limit is required"
                  >
                    <b-form-input
                      type="number"
                      v-model="form.passport_limit"
                      :state="getFieldState('passport_limit')"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>

          <!-- Carte : Social Links -->
          <b-card no-body class="mb-3">
            <b-card-header>{{ $t("Social Links") }}</b-card-header>
            <b-card-body>
              <b-row>
                <b-col md="3">
                  <b-form-group label="Website Link">
                    <b-form-input v-model="form.website_link" />
                  </b-form-group>
                </b-col>
                <b-col md="3">
                  <b-form-group label="Instagram Link">
                    <b-form-input v-model="form.instagram_link" />
                  </b-form-group>
                </b-col>
                <b-col md="3">
                  <b-form-group label="Facebook Link">
                    <b-form-input v-model="form.facebook_link" />
                  </b-form-group>
                </b-col>
                <b-col md="3">
                  <b-form-group label="TikTok Link">
                    <b-form-input v-model="form.tiktok_link" />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>

          <!-- Carte : Mail & Hive -->
          <b-card no-body class="mb-3">
            <b-card-header>{{ $t("Mail & Hive Configuration") }}</b-card-header>
            <b-card-body>
              <b-row>
                <b-col md="4">
                  <b-form-group
                    label="Hive URL"
                    :state="getFieldState('hive_url')"
                    invalid-feedback="Hive URL is required"
                  >
                    <b-form-input
                      v-model="form.hive_url"
                      :state="getFieldState('hive_url')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    label="Copy Mail"
                    :state="getFieldState('copy_mail')"
                    invalid-feedback="Copy Mail is required"
                  >
                    <b-form-input
                      type="email"
                      v-model="form.copy_mail"
                      :state="getFieldState('copy_mail')"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    label="Contact Mail"
                    :state="getFieldState('contact_mail')"
                    invalid-feedback="Contact Mail is required"
                  >
                    <b-form-input
                      type="email"
                      v-model="form.contact_mail"
                      :state="getFieldState('contact_mail')"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-form-group
                label="Brand's Mail Content"
                :state="getFieldState('brand_mail_content')"
                invalid-feedback="Mail content is required"
              >
                <b-form-textarea
                  v-model="form.brand_mail_content"
                  :state="getFieldState('brand_mail_content')"
                  rows="5"
                />
              </b-form-group>
            </b-card-body>
          </b-card>

        </b-col>
      </b-row>

    </b-form>

  </div>
</template>

<script>
import axios from "axios";
import CompaniesConnect from "./Connect.vue";
import FileManager from "../Common/FileManager.vue";

export default {
  name: "CompanyForm",
  components: {
    CompaniesConnect,
    FileManager
  },
  props: {
    company: {
      type: Object,
      default: () => ({})
    },
    social_links: {
      type: Array,
      default: () => []
    },
    connected_companies: {
      type: Array,
      default: () => []
    },
    countries: {
      type: Array,
      default: () => []
    },
    companyTypes: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      form: {
        id: this.company.id || null,
        name: this.company.name || "",
        type: this.company.type || "",
        company_code: this.company.company_code || "",
        address: this.company.address || "",
        city: this.company.city || "",
        zipcode: this.company.zipcode || "",
        country_id: this.company.country_id || "",
        production_place: this.company.production_place || "",
        with_passports: this.company.with_passports || false,
        passport_limit: this.company.passport_limit || "",
        companies: this.connected_companies || [],
        hive_url: this.company.hive_url || "",
        copy_mail: this.company.copy_mail || "",
        contact_mail: this.company.contact_mail || "",
        brand_mail_content: this.company.brand_mail_content || "",
        website_link:
          this.social_links?.find(link => link.social_type === "website")
            ?.link_url || "",
        instagram_link:
          this.social_links?.find(link => link.social_type === "instagram")
            ?.link_url || "",
        facebook_link:
          this.social_links?.find(link => link.social_type === "facebook")
            ?.link_url || "",
        tiktok_link:
          this.social_links?.find(link => link.social_type === "tiktok")
            ?.link_url || ""
      },
      productionPlaceOptions: [
        { value: "1", text: "Region" },
        { value: "2", text: "Country" },
        { value: "3", text: "City" }
      ],
      errors: {},
      loading: false
    };
  },
  computed: {
    isEditing() {
      return !!this.form.id;
    },
    formattedCountries() {
      return this.countries.map(country => ({
        value: country.id,
        text: country.name
      }));
    },
    formattedCompanyTypes() {
      return Object.entries(this.companyTypes).map(([key, val]) => ({
        value: val,
        text: key
      }));
    }
  },
  methods: {
    getFieldState(field) {
      return this.errors[field] === undefined ? null : false;
    },
    updateConnectedCompanies(selectedIds) {
      this.form.companies = selectedIds;
    },
    async handleSubmit() {
      this.loading = true;
      this.errors = {};

      try {
        const url = this.form.id
          ? `/api/companies/${this.form.id}`
          : `/api/companies`;
        const method = this.isEditing ? "put" : "post";

        const payload = { ...this.form };

        await axios[method](url, payload);

        this.$bvToast.toast(
          this.isEditing
            ? "Company updated successfully!"
            : "Company created successfully!",
          {
            title: "Success",
            variant: "success",
            solid: true
          }
        );

        setTimeout(() => {
          window.location.href = "/companies";
        }, 1000);
      } catch (error) {
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$bvToast.toast("An error occurred. Please try again.", {
            title: "Error",
            variant: "danger",
            solid: true
          });
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.left-sticky-card {
  position: sticky;
  top: 80px;
}
</style>
